// ============================================================================
// AdminTournamentTesting - one-click test-tournament wizard
// ============================================================================
// Single primary action: Start Test Tournament. Behind one button this
// creates a tournament with sensible defaults, seeds it with bots, and
// returns a Join link the admin can click immediately. A second card shows
// the most-recent test tournament with kill / re-seed actions inline.
// ============================================================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Heading, Text, HStack, VStack, Button, Alert, AlertIcon,
  NumberInput, NumberInputField, NumberInputStepper,
  NumberIncrementStepper, NumberDecrementStepper, FormControl, FormLabel,
  Select, Code, Divider, Switch,
} from '@chakra-ui/react';
import { Rocket, Skull, Play, Bot, Trash2, Mic } from 'lucide-react';
import HostTournamentModal from '../tournament/HostTournamentModal';
import gameApiService from '../../services/gameApiService';
import { POKER_COLORS } from '../../theme/designSystem';

interface ActiveTestTourney {
  id: string;
  name: string;
  startsAt: string;
  botCount: number;
  buyIn: number;
  currency: 'playcoins' | 'pkcash';
}

const AdminTournamentTesting: React.FC = () => {
  const navigate = useNavigate();

  // Wizard inputs (sensible defaults - most admins won't change them)
  const [botCount, setBotCount] = useState<number>(8);
  const [startInMinutes, setStartInMinutes] = useState<number>(2);
  const [buyIn, setBuyIn] = useState<number>(100);
  const [currency, setCurrency] = useState<'playcoins' | 'pkcash'>('playcoins');
  const [structure, setStructure] = useState<'regular' | 'turbo' | 'hyper' | 'deep'>('turbo');
  const [tableSize, setTableSize] = useState<number>(8);
  const [refundRealPlayers, setRefundRealPlayers] = useState(true);

  const [running, setRunning] = useState(false);
  const [killing, setKilling] = useState(false);
  const [seedingMore, setSeedingMore] = useState(false);
  const [purging, setPurging] = useState(false);
  const [generatingCatalog, setGeneratingCatalog] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [catalogDays, setCatalogDays] = useState<number>(1);
  const [active, setActive] = useState<ActiveTestTourney | null>(null);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);

  const generateCatalog = async () => {
    setGeneratingCatalog(true);
    const r = await gameApiService.generateDailyCatalog({ days: catalogDays });
    setGeneratingCatalog(false);
    if (r.success) {
      const perDayStr = (r.perDay || [])
        .map(d => `${d.date}: +${d.created}${d.skipped ? ` (${d.skipped} existed)` : ''}`)
        .join(' • ');
      const zombieMsg = (r as any).deletedZombies > 0
        ? ` · Cleaned ${(r as any).deletedZombies} stale past-start entries.`
        : '';
      setStatus({
        ok: true,
        msg: `Daily catalog: ${r.totalCreated} created, ${r.totalSkipped} already existed.${zombieMsg} ${perDayStr}`,
      });
    } else {
      setStatus({ ok: false, msg: r.error || 'Catalog generation failed' });
    }
  };

  /**
   * ONE BUTTON, WHOLE SCHEDULE. Builds the complete official line-up:
   * a full week of the daily catalog (the staggered prime-time events) plus
   * the weekly tier ladder (the recurring headline tournaments). Both are
   * idempotent server-side - anything already on the schedule is skipped,
   * so this is safe to press twice.
   */
  const generateAllOfficial = async () => {
    setGeneratingAll(true);
    setStatus(null);
    const [daily, weekly] = await Promise.all([
      gameApiService.generateDailyCatalog({ days: 7 }),
      gameApiService.generateWeeklyTournaments(),
    ]);
    setGeneratingAll(false);

    const parts: string[] = [];
    if (daily.success) {
      parts.push(`Daily catalog: ${daily.totalCreated ?? 0} created${daily.totalSkipped ? `, ${daily.totalSkipped} already existed` : ''}`);
    } else {
      parts.push(`Daily catalog FAILED: ${daily.error}`);
    }
    if (weekly.success) {
      parts.push(`Weekly ladder: ${weekly.created ?? 0} created${weekly.skipped ? `, ${weekly.skipped} skipped` : ''}`);
    } else {
      parts.push(`Weekly ladder FAILED: ${weekly.error}`);
    }
    setStatus({ ok: daily.success && weekly.success, msg: parts.join(' • ') });
  };

  const purgeAll = async () => {
    if (!window.confirm('Delete ALL test tournaments (isTest=true) and refund real entrants?')) return;
    setPurging(true);
    const r = await gameApiService.deleteAllTestTournaments();
    setPurging(false);
    if (r.success) {
      setStatus({
        ok: true,
        msg: `Cleared ${r.deleted} test tournaments (refunded ${r.refunded}, tables ended ${r.tablesEnded}).`,
      });
      setActive(null);
    } else {
      setStatus({ ok: false, msg: r.error || 'Cleanup failed' });
    }
  };

  const startTournament = async () => {
    setRunning(true);
    setStatus(null);
    setActive(null);

    // Step 1: create
    const create = await gameApiService.createTestTournament({
      startInMinutes,
      minPlayers: 2,
      currency,
      buyIn,
      structure,
      maxPlayersPerTable: tableSize,
    });
    if (!create.success || !create.tournamentId) {
      setRunning(false);
      setStatus({ ok: false, msg: create.error || 'Create failed' });
      return;
    }

    // Step 2: seed
    const seed = await gameApiService.seedTournamentBots(create.tournamentId, botCount);
    if (!seed.success) {
      setRunning(false);
      setStatus({
        ok: false,
        msg: `Tournament created (${create.tournamentId}) but seeding bots failed: ${seed.error}`,
      });
      setActive({
        id: create.tournamentId,
        name: create.name || 'Test Tournament',
        startsAt: create.startsAt || '',
        botCount: 0,
        buyIn,
        currency,
      });
      return;
    }

    setRunning(false);
    setActive({
      id: create.tournamentId,
      name: create.name || 'Test Tournament',
      startsAt: create.startsAt || '',
      botCount: seed.added || botCount,
      buyIn,
      currency,
    });
    setStatus({
      ok: true,
      msg: `Created ${create.name} with ${seed.added} bots. Starts in ${startInMinutes}m.`,
    });
  };

  const killActive = async () => {
    if (!active) return;
    if (!window.confirm(`Kill "${active.name}"? Bots removed, real entrants ${refundRealPlayers ? 'refunded' : 'NOT refunded'}, tournament cancelled.`)) return;
    setKilling(true);
    const r = await gameApiService.killTestTournament(active.id, refundRealPlayers);
    setKilling(false);
    if (r.success) {
      setStatus({
        ok: true,
        msg: `Killed. Bots: ${r.botsRemoved}, refunds: ${r.realPlayersRefunded}, tables ended: ${r.tablesEnded}.`,
      });
      setActive(null);
    } else {
      setStatus({ ok: false, msg: r.error || 'Kill failed' });
    }
  };

  const seedMoreBots = async () => {
    if (!active) return;
    setSeedingMore(true);
    const r = await gameApiService.seedTournamentBots(active.id, botCount);
    setSeedingMore(false);
    if (r.success) {
      setActive({ ...active, botCount: active.botCount + (r.added || 0) });
      setStatus({ ok: true, msg: `Added ${r.added} more bots. Total entrants: ${r.totalEntrants}.` });
    } else {
      setStatus({ ok: false, msg: r.error || 'Seed failed' });
    }
  };

  const joinTournament = () => {
    if (!active) return;
    navigate(`/tournaments`);
  };

  const [hostOpen, setHostOpen] = useState(false);
  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        {/* Real tournaments with voice/video, same form creators use */}
        <Box bg={`${POKER_COLORS.cyan}0D`} border="1px solid" borderColor={`${POKER_COLORS.cyan}44`} borderRadius="lg" p={4}>
          <HStack justify="space-between" flexWrap="wrap" gap={3}>
            <Box>
              <Heading size="sm" color={POKER_COLORS.cyan} mb={1}>Create a tournament (voice / video)</Heading>
              <Text color="gray.400" fontSize="sm">A real, listed tournament with the creator form: buy-in, speed, entry, and voice or video at the tables. No monthly limit for admins.</Text>
            </Box>
            <Button size="sm" colorScheme="cyan" leftIcon={<Mic size={14} />} onClick={() => setHostOpen(true)}>Create tournament</Button>
          </HStack>
          <HostTournamentModal
            isOpen={hostOpen}
            onClose={() => setHostOpen(false)}
            permissions={{ canHost: true, isAdmin: true, mediaAllowed: 'video', mediaComped: false }}
            quota={{ usedThisMonth: 0, freePerMonth: 2, nextNeedsApproval: false }}
            creatorGroupName={null}
          />
        </Box>
        <HStack justify="space-between" align="start">
          <Box>
            <Heading size="md" color={POKER_COLORS.cyan} mb={1}>Tournament Test</Heading>
            <Text color="gray.400" fontSize="sm">
              One click spins up a test tournament with bots and gives you a join link.
            </Text>
          </Box>
          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            leftIcon={<Trash2 size={14} />}
            onClick={purgeAll}
            isLoading={purging}
            loadingText="Purging"
          >
            Delete all test tournaments
          </Button>
        </HStack>

        {/* ── DAILY CATALOG GENERATOR ───────────────────────────────────── */}
        <Box
          bg={`${POKER_COLORS.gold}11`}
          border="1px solid"
          borderColor={`${POKER_COLORS.gold}55`}
          borderRadius="lg"
          p={6}
        >
          <VStack align="stretch" spacing={4}>
            <HStack>
              <Rocket size={20} color={POKER_COLORS.gold} />
              <Heading size="md" color="white">Daily Tournament Catalog</Heading>
            </HStack>
            <Text color="gray.400" fontSize="sm">
              Seeds 15 varied tournaments per day spanning every stake level - micro
              freerolls through $500 high-roller deep stacks. Mix of Hyper / Turbo /
              Regular / Deep, 6-Max, and GTD anchors at prime times. Idempotent -
              re-running for the same date is safe.
            </Text>
            <HStack spacing={3} align="end">
              <FormControl maxW="120px">
                <FormLabel color="gray.300" fontSize="xs">Days</FormLabel>
                <NumberInput value={catalogDays} min={1} max={7}
                  onChange={(_, n) => setCatalogDays(isNaN(n) ? 1 : n)}
                  bg="gray.900" borderColor="gray.600">
                  <NumberInputField color="white" />
                  <NumberInputStepper>
                    <NumberIncrementStepper color="gray.400" />
                    <NumberDecrementStepper color="gray.400" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <Button
                bg={POKER_COLORS.gold}
                color={POKER_COLORS.darkTeal}
                _hover={{ bg: '#fde68a' }}
                leftIcon={<Rocket size={16} />}
                onClick={generateCatalog}
                isLoading={generatingCatalog}
                loadingText="Generating..."
                fontWeight="bold"
              >
                Generate now
              </Button>
            </HStack>

            <Divider borderColor="gray.700" />

            {/* ONE BUTTON FOR THE WHOLE OFFICIAL SCHEDULE */}
            <Text color="gray.400" fontSize="sm">
              Or build the <b>entire official line-up</b> in one go: a full week of
              the daily catalog plus the weekly tier ladder (the recurring headline
              events). Idempotent - anything already scheduled is skipped, so it is
              safe to press more than once.
            </Text>
            <Button
              w="100%"
              size="lg"
              bg="linear-gradient(135deg, #f5d976, #d4af37 55%, #a97f1f)"
              color={POKER_COLORS.darkTeal}
              _hover={{ filter: 'brightness(1.08)' }}
              leftIcon={<Rocket size={18} />}
              onClick={generateAllOfficial}
              isLoading={generatingAll}
              loadingText="Building the schedule..."
              fontWeight="bold"
              letterSpacing="0.05em"
            >
              GENERATE ALL OFFICIAL TOURNAMENTS
            </Button>
          </VStack>
        </Box>

        {/* ── ONE-CLICK WIZARD ───────────────────────────────────────────── */}
        <Box
          bg={`${POKER_COLORS.cyan}11`}
          border="1px solid"
          borderColor={`${POKER_COLORS.cyan}55`}
          borderRadius="lg"
          p={6}
        >
          <VStack align="stretch" spacing={5}>
            <HStack>
              <Rocket size={20} color={POKER_COLORS.cyan} />
              <Heading size="md" color="white">Start Test Tournament</Heading>
            </HStack>

            <HStack align="end" spacing={3} flexWrap="wrap">
              <FormControl maxW="130px">
                <FormLabel color="gray.300" fontSize="xs">Bots</FormLabel>
                <NumberInput value={botCount} min={1} max={2000}
                  onChange={(_, n) => setBotCount(isNaN(n) ? 1 : n)}
                  bg="gray.900" borderColor="gray.600">
                  <NumberInputField color="white" />
                  <NumberInputStepper>
                    <NumberIncrementStepper color="gray.400" />
                    <NumberDecrementStepper color="gray.400" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl maxW="140px">
                <FormLabel color="gray.300" fontSize="xs">Starts in (min)</FormLabel>
                <NumberInput value={startInMinutes} min={1} max={60}
                  onChange={(_, n) => setStartInMinutes(isNaN(n) ? 1 : n)}
                  bg="gray.900" borderColor="gray.600">
                  <NumberInputField color="white" />
                  <NumberInputStepper>
                    <NumberIncrementStepper color="gray.400" />
                    <NumberDecrementStepper color="gray.400" />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl maxW="140px">
                <FormLabel color="gray.300" fontSize="xs">Currency</FormLabel>
                <Select value={currency}
                  onChange={e => setCurrency(e.target.value as 'playcoins' | 'pkcash')}
                  bg="gray.900" borderColor="gray.600" color="white">
                  <option value="playcoins" style={{ background: '#1a202c' }}>PlayCoins</option>
                  <option value="pkcash" style={{ background: '#1a202c' }}>PKCash</option>
                </Select>
              </FormControl>

              <FormControl maxW="160px">
                <FormLabel color="gray.300" fontSize="xs">Structure</FormLabel>
                <Select value={structure}
                  onChange={e => setStructure(e.target.value as 'regular' | 'turbo' | 'hyper' | 'deep')}
                  bg="gray.900" borderColor="gray.600" color="white">
                  <option value="regular" style={{ background: '#1a202c' }}>Regular (10m levels)</option>
                  <option value="turbo" style={{ background: '#1a202c' }}>Turbo (5m levels)</option>
                  <option value="hyper" style={{ background: '#1a202c' }}>Hyper (2m levels)</option>
                  <option value="deep" style={{ background: '#1a202c' }}>Deep (15m levels)</option>
                </Select>
              </FormControl>

              <FormControl maxW="130px">
                <FormLabel color="gray.300" fontSize="xs">Table size</FormLabel>
                <Select value={tableSize}
                  onChange={e => setTableSize(Number(e.target.value))}
                  bg="gray.900" borderColor="gray.600" color="white">
                  <option value={6} style={{ background: '#1a202c' }}>6-Max</option>
                  <option value={8} style={{ background: '#1a202c' }}>8-Max</option>
                  <option value={9} style={{ background: '#1a202c' }}>9-Max</option>
                </Select>
              </FormControl>

              <FormControl maxW="120px">
                <FormLabel color="gray.300" fontSize="xs">Buy-in</FormLabel>
                <NumberInput value={buyIn} min={0} max={10000}
                  onChange={(_, n) => setBuyIn(isNaN(n) ? 0 : n)}
                  bg="gray.900" borderColor="gray.600">
                  <NumberInputField color="white" />
                </NumberInput>
              </FormControl>
            </HStack>

            <Button
              size="lg"
              h="56px"
              bg={POKER_COLORS.cyan}
              color={POKER_COLORS.darkTeal}
              _hover={{ bg: POKER_COLORS.accentCyan, transform: 'translateY(-1px)' }}
              _active={{ transform: 'translateY(0)' }}
              leftIcon={<Rocket size={18} />}
              fontWeight="bold"
              fontSize="md"
              onClick={startTournament}
              isLoading={running}
              loadingText="Spinning up..."
              isDisabled={!!active}
            >
              {active ? 'Tournament running below ↓' : 'Start Test Tournament'}
            </Button>
          </VStack>
        </Box>

        {/* ── ACTIVE TOURNAMENT CONTROL PANEL ────────────────────────────── */}
        {active && (
          <Box
            bg={POKER_COLORS.cardBg}
            border="1px solid"
            borderColor={`${POKER_COLORS.gold}55`}
            borderRadius="lg"
            p={6}
          >
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <HStack>
                    <Play size={16} color={POKER_COLORS.gold} />
                    <Text color={POKER_COLORS.gold} fontSize="sm" fontWeight="bold" letterSpacing="wide">
                      RUNNING
                    </Text>
                  </HStack>
                  <Text color="white" fontSize="lg" fontWeight="bold">{active.name}</Text>
                </VStack>
                <Button
                  size="md"
                  bg={POKER_COLORS.cyan}
                  color={POKER_COLORS.darkTeal}
                  _hover={{ bg: POKER_COLORS.accentCyan }}
                  leftIcon={<Play size={14} />}
                  onClick={joinTournament}
                  fontWeight="bold"
                >
                  Open lobby
                </Button>
              </HStack>

              <HStack spacing={6} fontSize="sm" color="gray.400" flexWrap="wrap">
                <Box>
                  <Text color="gray.500" fontSize="xs">Bots seated</Text>
                  <Text color="white" fontWeight="bold">{active.botCount}</Text>
                </Box>
                <Box>
                  <Text color="gray.500" fontSize="xs">Starts at</Text>
                  <Text color="white" fontWeight="bold">
                    {active.startsAt ? new Date(active.startsAt).toLocaleTimeString() : '-'}
                  </Text>
                </Box>
                <Box>
                  <Text color="gray.500" fontSize="xs">Buy-in</Text>
                  <Text color="white" fontWeight="bold">
                    {active.buyIn} {active.currency === 'playcoins' ? 'PC' : 'PKCash'}
                  </Text>
                </Box>
                <Box flex={1} minW={0}>
                  <Text color="gray.500" fontSize="xs">Tournament ID</Text>
                  <Code fontSize="xs" bg="transparent" color="cyan.300" wordBreak="break-all">
                    {active.id}
                  </Code>
                </Box>
              </HStack>

              <Divider borderColor={`${POKER_COLORS.cyan}22`} />

              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor={`${POKER_COLORS.cyan}66`}
                  color={POKER_COLORS.cyan}
                  _hover={{ bg: `${POKER_COLORS.cyan}22` }}
                  leftIcon={<Bot size={14} />}
                  onClick={seedMoreBots}
                  isLoading={seedingMore}
                >
                  Add {botCount} more bots
                </Button>
                <Box flex={1} />
                <HStack>
                  <Text color="gray.500" fontSize="xs">Refund real players on kill</Text>
                  <Switch
                    size="sm"
                    colorScheme="cyan"
                    isChecked={refundRealPlayers}
                    onChange={e => setRefundRealPlayers(e.target.checked)}
                  />
                </HStack>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  leftIcon={<Skull size={14} />}
                  onClick={killActive}
                  isLoading={killing}
                >
                  Kill
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}

        {/* ── STATUS / RESULT LINE ───────────────────────────────────────── */}
        {status && (
          <Alert
            status={status.ok ? 'success' : 'error'}
            bg={status.ok ? 'green.900' : 'red.900'}
            color="white"
          >
            <AlertIcon />
            <Text fontSize="sm">{status.msg}</Text>
          </Alert>
        )}

        {/* ── MANUAL KILL BY ID (for tournaments not in current session) ─ */}
        {!active && (
          <Box bg="gray.900" border="1px solid" borderColor="gray.800" borderRadius="md" p={4}>
            <Text color="gray.500" fontSize="xs" mb={1}>Need to kill a tournament from a different session?</Text>
            <ManualKillForm onResult={r => setStatus(r)} />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

const ManualKillForm: React.FC<{ onResult: (r: { ok: boolean; msg: string }) => void }> = ({ onResult }) => {
  const [tid, setTid] = useState('');
  const [killing, setKilling] = useState(false);
  return (
    <HStack>
      <NumberInput value={tid} onChange={v => setTid(String(v || ''))} flex={1} bg="gray.900">
        <NumberInputField
          value={tid}
          onChange={(e: any) => setTid(e.target.value)}
          placeholder="paste tournament id"
          fontFamily="mono"
          color="white"
          borderColor="gray.700"
        />
      </NumberInput>
      <Button
        size="sm"
        variant="outline"
        colorScheme="red"
        isLoading={killing}
        onClick={async () => {
          if (!tid.trim()) return;
          if (!window.confirm(`Kill tournament ${tid}?`)) return;
          setKilling(true);
          const r = await gameApiService.killTestTournament(tid.trim(), true);
          setKilling(false);
          if (r.success) {
            onResult({ ok: true, msg: `Killed ${tid}. Bots removed: ${r.botsRemoved}.` });
            setTid('');
          } else {
            onResult({ ok: false, msg: r.error || 'Kill failed' });
          }
        }}
      >
        Kill
      </Button>
    </HStack>
  );
};

export default AdminTournamentTesting;
